import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MapPin, Clock, ArrowRight } from 'lucide-react';
import { useLocation } from 'wouter';
import type { Region, Locality } from '@shared/schema';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { queryClient, apiRequest, fetchJson } from '@/lib/queryClient';
import { POST_LOGIN_RETURN_KEY } from '@/lib/authUtils';
import { useGeographyContext } from '@/hooks/useGeographyContext';
import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Indiana/Indianapolis', label: 'Indiana — Eastern, no DST' },
  { value: 'America/Phoenix', label: 'Arizona — Mountain, no DST' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
];

function resolveDefaultTimezone(): string {
  try {
    const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (US_TIMEZONES.some(tz => tz.value === browser)) return browser;
  } catch {
    // ignore
  }
  return 'America/New_York';
}

function consumePostOnboardingReturnPath(): string {
  const returnTo = window.sessionStorage.getItem(POST_LOGIN_RETURN_KEY);
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return '/';
  }
  window.sessionStorage.removeItem(POST_LOGIN_RETURN_KEY);
  return returnTo === '/onboarding' ? '/' : returnTo;
}

export default function Onboarding() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { setGeography } = useGeographyContext();

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ['active-regions'],
    queryFn: () => fetchJson<Region[]>('/api/regions'),
    staleTime: 1000 * 60 * 10,
  });

  const [selectedRegionId, setSelectedRegionId] = useState<string>(user?.preferredRegionId ?? '');

  const selectedRegion = regions.find(r => r.id === selectedRegionId);
  const isPhysical = selectedRegion?.type === 'physical';

  const { data: localities = [] } = useQuery<Locality[]>({
    queryKey: ['localities', selectedRegion?.slug],
    queryFn: () => fetchJson<Locality[]>(`/api/regions/${selectedRegion!.slug}/localities`),
    enabled: !!selectedRegion && isPhysical,
    staleTime: 1000 * 60 * 10,
  });

  const [form, setForm] = useState({
    preferredRegionId: user?.preferredRegionId ?? '',
    preferredLocalityId: user?.preferredLocalityId ?? '',
    timezone: user?.timezone && user.timezone !== 'UTC' ? user.timezone : resolveDefaultTimezone(),
    emailEnrollmentConfirmation: user?.emailEnrollmentConfirmation ?? true,
    emailClassReminders: user?.emailClassReminders ?? true,
    emailCreatorUpdates: user?.emailCreatorUpdates ?? true,
    emailAccountNotifications: user?.emailAccountNotifications ?? true,
  });

  useEffect(() => {
    if (user?.hasCompletedOnboarding) {
      navigate(consumePostOnboardingReturnPath());
    }
  }, [navigate, user?.hasCompletedOnboarding]);

  useEffect(() => {
    if (!user) return;
    setForm({
      preferredRegionId: user.preferredRegionId ?? '',
      preferredLocalityId: user.preferredLocalityId ?? '',
      timezone: user.timezone && user.timezone !== 'UTC' ? user.timezone : resolveDefaultTimezone(),
      emailEnrollmentConfirmation: user.emailEnrollmentConfirmation ?? true,
      emailClassReminders: user.emailClassReminders ?? true,
      emailCreatorUpdates: user.emailCreatorUpdates ?? true,
      emailAccountNotifications: user.emailAccountNotifications ?? true,
    });
    setSelectedRegionId(user.preferredRegionId ?? '');
  }, [user]);

  const onboardingMutation = useMutation({
    mutationFn: async (values: typeof form) => {
      const response = await apiRequest('PUT', '/api/auth/profile', {
        ...values,
        hasCompletedOnboarding: true,
      });
      return await response.json();
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      const region = regions.find(r => r.id === variables.preferredRegionId);
      const locality = localities.find(l => l.id === variables.preferredLocalityId);
      if (region) {
        setGeography(region.slug, locality?.slug ?? undefined);
      }
      toast({ title: 'Preferences saved', description: 'Your account setup is complete.' });
      navigate(consumePostOnboardingReturnPath());
    },
    onError: (error: Error) => {
      toast({
        title: 'Setup failed',
        description: error.message || 'Unable to save onboarding preferences.',
        variant: 'destructive',
      });
    },
  });

  const completeWithSkip = () => {
    onboardingMutation.mutate(form);
  };

  if (!user) return null;

  return (
    <PublicShell routeKey="onboarding">
      <main className="mx-auto flex min-h-[70vh] max-w-3xl items-center px-4 py-12 sm:px-6 lg:px-8">
        <Card className="w-full border-border/60 shadow-sm">
          <CardHeader className="space-y-3">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <MapPin className="h-6 w-6" />
            </div>
            <CardTitle className="text-3xl">Finish Setting Up Your Account</CardTitle>
            <CardDescription className="text-base">
              Tell us where you want to explore classes and set your timezone so we can show the
              right sessions and send reminders at the right time.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <form
              className="space-y-5"
              onSubmit={event => {
                event.preventDefault();
                onboardingMutation.mutate(form);
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="onboarding-region">Where do you want to explore classes?</Label>
                <Select
                  value={form.preferredRegionId}
                  onValueChange={value => {
                    setSelectedRegionId(value);
                    setForm(current => ({
                      ...current,
                      preferredRegionId: value,
                      preferredLocalityId: '',
                    }));
                  }}
                >
                  <SelectTrigger id="onboarding-region" data-testid="select-onboarding-region">
                    <SelectValue placeholder="Select a region" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map(region => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isPhysical && localities.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="onboarding-locality">Specific area (optional)</Label>
                  <Select
                    value={form.preferredLocalityId || 'all'}
                    onValueChange={value =>
                      setForm(current => ({
                        ...current,
                        preferredLocalityId: value === 'all' ? '' : value,
                      }))
                    }
                  >
                    <SelectTrigger
                      id="onboarding-locality"
                      data-testid="select-onboarding-locality"
                    >
                      <SelectValue placeholder="Select a specific area" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All of {selectedRegion?.name}</SelectItem>
                      {localities.map(locality => (
                        <SelectItem key={locality.id} value={locality.id}>
                          {locality.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="onboarding-timezone">Timezone</Label>
                <Select
                  value={form.timezone}
                  onValueChange={value => setForm(current => ({ ...current, timezone: value }))}
                >
                  <SelectTrigger
                    id="onboarding-timezone"
                    data-testid="select-onboarding-timezone"
                    className="gap-2"
                  >
                    <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <SelectValue placeholder="Select your timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4 rounded-xl border border-border/60 bg-muted/30 p-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Notification Preferences
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Choose which class updates we should send you.
                  </p>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="emailEnrollmentConfirmation">Enrollment confirmations</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive confirmations when you book a class.
                    </p>
                  </div>
                  <Switch
                    id="emailEnrollmentConfirmation"
                    checked={form.emailEnrollmentConfirmation}
                    onCheckedChange={checked =>
                      setForm(current => ({ ...current, emailEnrollmentConfirmation: checked }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="emailClassReminders">Class reminders</Label>
                    <p className="text-sm text-muted-foreground">
                      Get reminder emails before your upcoming sessions.
                    </p>
                  </div>
                  <Switch
                    id="emailClassReminders"
                    checked={form.emailClassReminders}
                    onCheckedChange={checked =>
                      setForm(current => ({ ...current, emailClassReminders: checked }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="emailCreatorUpdates">Creator updates</Label>
                    <p className="text-sm text-muted-foreground">
                      Hear about new classes from creators you follow.
                    </p>
                  </div>
                  <Switch
                    id="emailCreatorUpdates"
                    checked={form.emailCreatorUpdates}
                    onCheckedChange={checked =>
                      setForm(current => ({ ...current, emailCreatorUpdates: checked }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="emailAccountNotifications">Account notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Keep account notices enabled for payment, refund, and security updates.
                    </p>
                  </div>
                  <Switch
                    id="emailAccountNotifications"
                    checked={form.emailAccountNotifications}
                    onCheckedChange={checked =>
                      setForm(current => ({ ...current, emailAccountNotifications: checked }))
                    }
                  />
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-muted/40 p-4 text-sm text-muted-foreground">
                You can update these details later from your account page. Skipping now still marks
                onboarding complete.
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  type="submit"
                  disabled={onboardingMutation.isPending}
                  className="sm:flex-1"
                  data-testid="button-complete-onboarding"
                >
                  <ArrowRight className="mr-2 h-4 w-4" />
                  {onboardingMutation.isPending ? 'Saving...' : 'Complete Setup'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={onboardingMutation.isPending}
                  onClick={completeWithSkip}
                  data-testid="button-skip-onboarding"
                >
                  Skip for now
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </PublicShell>
  );
}
